<template>
  <div v-if="isFileFetching" class="tw:absolute tw:top-0 tw:left-0 tw:w-full tw:h-full tw:z-10 tw:flexcenter">
    <div class="tw:bg-white tw:rounded-base tw:px-4 tw:py-3.5">
      <div class="tw:flexrow-3 tw:flexcenter">
        <main-loader :size="19" :width="2" />
        <span class="tw:font-medium tw:text-sm tw:text-gray-700">{{ $i18n.t('default.loadingData') }}</span>
      </div>
    </div>
  </div>
  <label
    v-else
    ref="dropzone"
    id="drop-zone"
    @drop="drop"
    @dragover="dragover"
    @dragenter="$event => $event.stopPropagation()"
    @dragleave="isOver = false"
    :style="{
      backgroundImage: customBorder,
    }"
    class="tw:absolute tw:top-0 tw:left-0 tw:w-full tw:h-full tw:z-10 tw:rounded-base tw:bg-[rgba(203,224,250,0.3)]"
    :class="{ 'tw:block': isOver, 'tw:hidden': !isOver }"
  >
    <div class="tw:h-full tw:w-full tw:flexcenter tw:font-medium tw:text-lg"></div>
    <input type="file" id="file-input" :accept="availableFormats.join(',')" class="tw:hidden" />
    <session-save-dialog
      v-if="saveDialogVisible !== null"
      :is-visible.sync="saveDialogVisible"
      :session-id="sessionId"
    />
  </label>
</template>

<script>
import { get, call } from 'vuex-pathify';
import { customDashedBorderUrl } from '@/assets/js/utility';

import MainLoader from '@/components/MainLoader';

export default {
  name: 'MapDropSpace',
  components: {
    MainLoader,
    SessionSaveDialog: () => import('@/components/SessionSaveDialog'),
  },
  data: () => ({
    isFileFetching: false,
    isOver: false,

    saveDialogVisible: null,
    sessionId: null,
  }),
  computed: {
    availableFormats() {
      return ['.dxf', '.geojson', '.gpkg'];
    },
    customBorder() {
      return customDashedBorderUrl({
        color: this.$_colors.primary.replace('#', '%23'),
        dashArray: [16, 16],
        width: 6,
      });
    },
    sessions: get('fileSession/sessions'),
  },
  methods: {
    getFileExtension(file) {
      return `.${file.name.split('.').at(-1)}`;
    },

    deleteSession: call('fileSession/deleteSession'),
    addSession: call('fileSession/addSession'),
    async processFiles(files) {
      this.sessionId = await this.addSession({ files });
      const session = this.sessions[this.sessionId];
      if (!session?.layers?.length) {
        this.$store.set('snackbar/PUSH_MESSAGE!', { message: this.$i18n.t('errors.sessionNoLayers') });
        this.deleteSession({ sessionId: this.sessionId });
      } else this.saveDialogVisible = true;
    },

    /** Dropzone events */
    async drop(e) {
      /** Do not proceed if format is wrong - it is read from name extension because type is not accessible from different OS and browsers */
      const files = Array.from(e.dataTransfer.files || [])?.filter(file =>
        this.availableFormats.includes(this.getFileExtension(file))
      );
      if (!files?.length) return;

      this.isFileFetching = true;
      try {
        await this.processFiles(files);
        this.isOver = false;
      } catch (error) {
        console.error(error);
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('snackbar.dragDropDataError'),
        });
      } finally {
        this.isFileFetching = false;
      }
    },
    dragover(e) {
      const fileItems = [...e.dataTransfer.items].filter(item => item.kind === 'file');
      if (!fileItems.length) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = fileItems?.length ? 'copy' : 'none';
    },

    /** Parent events */
    parentDragenter() {
      this.isOver = true;
    },
    windowDrop(e) {
      e.preventDefault();
      this.isOver = false;
    },
    windowDragover(e) {
      e.preventDefault();
    },
    parentDragover(e) {
      const fileItems = [...e.dataTransfer.items].filter(item => item.kind === 'file');
      if (!fileItems.length) return;
      e.preventDefault();
      if (!this.$refs.dropzone?.contains(e.target)) e.dataTransfer.dropEffect = 'none';
    },

    /** Events handler */
    handleParentEvents(remove = false) {
      const parentElement = this.$parent.map?.getViewport();
      if (!parentElement) return console.warn('Map viewport not found');
      [
        [parentElement, 'dragenter', this.parentDragenter],
        [parentElement, 'dragover', this.parentDragover],
        [window, 'drop', this.windowDrop, false],
        [window, 'dragover', this.windowDragover, false],
      ].forEach(([element, ev, method, capture]) =>
        element[remove ? 'removeEventListener' : 'addEventListener'](ev, method, capture)
      );
    },
  },
  mounted() {
    this.$nextTick(() => this.handleParentEvents());
    this.$parent.$once('hook:beforeDestroy', () => this.handleParentEvents(true));
  },
};
</script>
